import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  BookOpen, Save, Eye, ArrowLeft, Loader2, Image as ImageIcon,
  Wand2, X, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import * as api from '../services/api';

const TECH_FONT = "'Inter', 'SF Pro Display', sans-serif";

const BLOG_CATEGORIES = [
  { value: 'opinion', label: 'Opinión' },
  { value: 'tutorial', label: 'Tutoriales' },
  { value: 'analisis', label: 'Análisis' },
  { value: 'tendencias', label: 'Tendencias' },
  { value: 'general', label: 'General' }
];

const BlogEditorPage = () => {
  const navigate = useNavigate();
  const { blogId } = useParams();
  const { user, isStaff, loading: authLoading } = useAuth();
  
  const isEditing = blogId && blogId !== 'new';
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [form, setForm] = useState({
    title: '',
    content: '',
    excerpt: '',
    cover_image: '',
    category: 'general',
    tags: [],
    status: 'draft'
  });
  const [tagInput, setTagInput] = useState('');
  const [imagePrompt, setImagePrompt] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!isStaff) {
      navigate('/');
      return;
    }
    if (isEditing) {
      fetchPost();
    }
  }, [isStaff, authLoading, navigate, isEditing, blogId]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const data = await api.getBlogPost(blogId);
      setForm({
        title: data.title || '',
        content: data.content || '',
        excerpt: data.excerpt || '',
        cover_image: data.cover_image || '',
        category: data.category || 'general',
        tags: data.tags || [],
        status: data.status || 'draft'
      });
    } catch (error) {
      console.error('Error fetching post:', error);
      setError('No se pudo cargar el post');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (publishNow = false) => {
    if (!form.title.trim()) {
      setError('El título es requerido');
      return;
    }
    if (!form.content.trim()) {
      setError('El contenido es requerido');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const submitData = {
        ...form,
        status: publishNow ? 'published' : form.status
      };

      if (isEditing) {
        await api.updateBlogPost(blogId, submitData);
      } else {
        await api.createBlogPost(submitData);
      }

      navigate('/editor/my-content');
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al guardar el post');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateImage = async () => {
    const prompt = imagePrompt.trim() || `Imagen de portada para blog post: ${form.title}. Estilo moderno, tecnológico, profesional.`;
    
    setGeneratingImage(true);
    setError('');

    try {
      const response = await api.generateStandaloneImage({ prompt });
      // Backend returns image_url
      if (response.image_url) {
        setForm({ ...form, cover_image: response.image_url });
        setImagePrompt('');
      } else if (response.url) {
        setForm({ ...form, cover_image: response.url });
        setImagePrompt('');
      }
    } catch (error) {
      console.error('Error generating image:', error);
      setError('Error al generar la imagen. Intenta de nuevo.');
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      setForm({ ...form, tags: [...form.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setForm({ ...form, tags: form.tags.filter(t => t !== tagToRemove) });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="blog-editor-page">
      {/* Header */}
      <div className="bg-[#0f172a] text-white py-6">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/editor/my-content" className="text-gray-400 hover:text-cyan-400">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold" style={{ fontFamily: TECH_FONT }}>
                  {isEditing ? 'Editar Post' : 'Nuevo Post de Blog'}
                </h1>
                <p className="text-gray-400 text-sm">
                  Comparte tu opinión con la comunidad
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="border-gray-600 text-gray-300"
                onClick={() => handleSubmit(false)}
                disabled={saving}
              >
                <Save className="w-4 h-4 mr-2" />
                Guardar Borrador
              </Button>
              <Button 
                className="bg-cyan-600 hover:bg-cyan-700"
                onClick={() => handleSubmit(true)}
                disabled={saving}
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                Publicar
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Título *
            </label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Escribe un título atractivo..."
              className="text-lg"
              data-testid="blog-title-input"
            />
          </div>

          {/* Excerpt */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Extracto
            </label>
            <Textarea
              value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              placeholder="Un breve resumen del post (aparece en las tarjetas de vista previa)..."
              rows={2}
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contenido *
            </label>
            <Textarea
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              placeholder="Escribe el contenido de tu post..."
              rows={15}
              className="font-mono"
              data-testid="blog-content-input"
            />
            <p className="text-xs text-gray-400 mt-1">
              Puedes usar saltos de línea para formatear el texto
            </p>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Categoría
            </label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="w-full md:w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BLOG_CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Cover Image with AI Generation */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              Imagen de Portada
            </label>
            
            {/* AI Generation Box */}
            <div className="bg-gradient-to-r from-purple-50 to-cyan-50 border border-purple-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-purple-500" />
                <span className="font-medium text-purple-700">Generar con IA</span>
              </div>
              <div className="flex gap-2">
                <Input
                  value={imagePrompt}
                  onChange={(e) => setImagePrompt(e.target.value)}
                  placeholder="Describe la imagen que deseas... (o deja vacío para generar automáticamente)"
                  className="flex-1"
                  disabled={generatingImage}
                />
                <Button 
                  onClick={handleGenerateImage}
                  disabled={generatingImage || !form.title.trim()}
                  className="bg-gradient-to-r from-purple-500 to-cyan-500 hover:from-purple-600 hover:to-cyan-600 text-white min-w-[140px]"
                  data-testid="generate-image-btn"
                >
                  {generatingImage ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generando...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4 mr-2" />
                      Generar Imagen
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-purple-600 mt-2">
                💡 La IA creará una imagen única basada en el título del post o tu descripción personalizada
              </p>
            </div>

            {/* Manual URL Input */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-400">o</span>
              <Input
                value={form.cover_image}
                onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                placeholder="Pega la URL de una imagen existente..."
                className="flex-1"
              />
            </div>

            {/* Preview */}
            {form.cover_image && (
              <div className="relative">
                <img 
                  src={form.cover_image} 
                  alt="Preview" 
                  className="w-full max-h-64 object-cover rounded-lg shadow-md"
                  onError={(e) => e.target.style.display = 'none'}
                />
                <button
                  onClick={() => setForm({ ...form, cover_image: '' })}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Etiquetas
            </label>
            <div className="flex gap-2 mb-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="Agregar etiqueta..."
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
              />
              <Button type="button" onClick={handleAddTag} variant="outline">
                Agregar
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.tags.map((tag, idx) => (
                <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                  {tag}
                  <button onClick={() => handleRemoveTag(tag)} className="ml-1 hover:text-red-500">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogEditorPage;
